import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<string, Record<string, string>> = {
  en: {
    'nav.whatIsPaca': 'Information',
    'nav.joinTherapist': 'Join us',
    'nav.pricing': 'Pricing',
    'nav.sharePaca': 'Share',
    'nav.therapistPortal': 'Therapist Portal',
    'nav.backHome': 'Back to Home',
    'search.placeholder': 'Search therapist by name or phone...',
    'chat.title': 'PACA Chat',
    'chat.placeholder': 'Type your question or request here...',
    'chat.welcome': 'Hello! I\'m PACA, your digital health assistant. I see you\'ve chosen to chat with',
    'chat.selectTherapist': 'Hi, I\'m the PACA system. I can help you find the right therapist for your needs. Let\'s start - what\'s your full name?',
    'chat.howCanHelp': 'How can I help you today?',
    'chat.error': 'Sorry, there\'s a technical issue. Please try again.',
    'chat.voiceRecorded': 'Voice message recorded...',
    'chat.newChat': 'New Chat',
    'about.title': 'Information',
    'about.desc1': 'PACA is a communication system between patients and therapists in medicine. All types of medicine. What makes PACA special is that it becomes your personal interactive assistant after a short registration of a few minutes at no cost. The information you provide to it and only that information, PACA will convey in a pleasant and patient manner to anyone who asks it 24 hours a day, 7 days a week in any language they ask. If it is asked a question for which it does not have enough information to answer, it will ask you and return an answer to the patient. All through WhatsApp messages. No repetitive patient questions, no phone confusion and no cumbersome phone system. At any hour, at any time and patients are satisfied! Join us for free and save precious time...',
    'therapist.title': 'Therapist Portal',
    'therapist.login': 'Log in to access your personal area',
    'therapist.username': 'Username (Email)',
    'therapist.password': 'Password',
    'therapist.loginBtn': 'Login',
    'therapist.loginBtnLoading': 'Logging in...',
    'therapist.logout': 'Logout',
    'therapist.welcome': 'Welcome',
    'therapist.error': 'Error',
    'therapist.loginError': 'Login Error',
    'therapist.loginSuccess': 'Login Successful',
    'therapist.usernameRequired': 'Please enter username and password',
    'therapist.wrongCredentials': 'Incorrect username or password',
    'therapist.loginFailed': 'An error occurred during login',
    'therapist.tabs.addNote': 'Add Note',
    'therapist.tabs.retakeSurvey': 'Retake Survey',
    'therapist.tabs.editDetails': 'Edit Details',
    'therapist.tabs.analytics': 'Analytics',
    'therapist.tabs.reviews': 'Reviews',
    'therapist.tabs.addRecommendations': 'Add Recommendations',
    'therapist.tabs.subscription': 'Subscription',
    'pricing.title': 'Pricing Plans',
    'pricing.desc': 'The app is free for patients and beginning therapists',
    'pricing.basic': 'Basic Package',
    'pricing.free': 'Free!',
    'pricing.basicDesc': 'Free for therapists',
    'pricing.noCreditCard': 'No credit card required',
    'pricing.feature1': '24/7 patient support in multiple languages',
    'pricing.feature2': 'Smart chat with patients',
    'pricing.feature3': 'Therapist profile management',
    'pricing.business': 'Business Package',
    'pricing.businessPrice': '$8',
    'pricing.businessPriceUnit': '/month',
    'pricing.businessDesc': 'Professional tools for growing practices',
    'pricing.businessFeature1': 'CRM referral integration',
    'pricing.businessFeature2': 'Save all inquiries - never lose leads',
    'pricing.businessFeature3': 'Fast system response',
    'pricing.businessFeature4': 'Business hours support',
    'pricing.businessFeature5': 'Therapist marketing',
    'pricing.premium': 'Premium Package',
    'pricing.premiumPrice': '$250',
    'pricing.premiumPriceUnit': '/month',
    'pricing.premiumDesc': 'Complete solution with AI avatar',
    'pricing.premiumFeature1': 'Personal AI avatar',
    'pricing.premiumFeature2': '24/7 patient support',
    'pricing.premiumFeature3': 'Digital marketing',
    'pricing.premiumFeature4': 'AI recommendations for image improvement',
    'pricing.premiumFeature5': 'Brand image enhancement',
    'pricing.premiumFeature6': 'Multiple CRM & AI secretary integrations',
    'pricing.allBasicFeatures': 'All basic features',
    'pricing.allBusinessFeatures': 'All business features',
    'pricing.whyPaca': 'Why PACA?',
    'pricing.whyPacaDesc': 'Because we believe in serving the patient and therapist community in complementary medicine. The site is designed to save you many hours of explaining your working method to patients and how you can help them.',
    'pricing.costCalculator': 'Cost and Time Calculator',
    'pricing.costs': 'Costs',
    'pricing.costExample1': 'Basic: Free - unlimited patient questions',
    'pricing.costExample2': 'Business: $8/month - CRM integration & marketing',
    'pricing.costExample3': 'Premium: $250/month - Full AI avatar & marketing suite',
    'pricing.timeSaved': 'Time Saved',
    'pricing.timeExample1': 'Each question saves you about half a minute of explanation',
    'pricing.timeExample2': '100 questions = 50 minutes saved per month',
    'pricing.timeExample3': '200 questions = 100 minutes (1.7 hours) saved per month',
    'pricing.signUpFree': 'Sign Up Free',
    'pricing.upgradeBusiness': 'Upgrade to Business',
    'pricing.upgradePremium': 'Upgrade to Premium',
    'share.title': 'Share PACA',
    'share.desc': 'Help others discover our system',
    'share.email': 'Email',
    'share.message': 'Check out PACA - Advanced communication system between patients and therapists',
    'share.emailSubject': 'PACA - Medical Communication System',
    'share.howToShare': 'How to Share?',
    'share.howToShareDesc': 'Choose your preferred platform and share PACA with friends and colleagues',
    'share.tips': 'Sharing Tips',
    'share.tip1': 'Share with therapist colleagues who can benefit from the system',
    'share.tip2': 'Tell about your personal experience with PACA',
    'share.tip3': 'Emphasize the value the system brings to patients and therapists',
    'notFound.title': 'Page Not Found',
    'notFound.desc': 'The page you are looking for does not exist',
    'registration.title': 'Therapist Registration',
    'registration.desc': 'Join our network of healthcare professionals',
    // Registration bot translations
    'registration.bot.welcome': 'Hi! 👋 I\'m PACA, the smart registration assistant of the system.',
    'registration.bot.start': 'Let\'s start your registration process as a therapist in the PACA system.',
    'registration.bot.instructions': '💡 At any step you can type "start over" or "go back" to navigate between steps',
    'registration.step.phone': 'Please enter your phone number (10 digits):',
    'registration.step.fullName': 'What is your full name?',
    'registration.step.personalInfo': 'Tell me about yourself - age, place of residence, specialization, education',
    'registration.step.treatmentMethod': 'What treatment method do you work with? What makes it unique from other treatments?',
    'registration.step.treatmentTypes': 'What are the different treatments you provide? (what they help with, how long they take, how much they cost)',
    'registration.step.website': 'Do you have a website? Send a link or write the address (or write "no" if you don\'t have one)',
    'registration.step.workLocation': 'Where do you work? (at home, your place, on Zoom? address? is there parking?)',
    'registration.step.contactDetails': 'Details for scheduling appointments (your phone, secretary\'s, workplace, phone for appointments)',
    'registration.step.additionalInfo': 'Additional information for patients',
    'registration.step.recommendations': 'If you have recommendations online, send me links so I can add them',
    'registration.step.profileImage': 'You can send a profile picture if you want. If you don\'t send a picture, I\'ll add the PACA logo as default which you can change later on the website.',
    'registration.step.video': 'You can film yourself for up to 30 seconds talking about yourself, how you treat or anything you\'d like to say to patients.',
    'registration.step.email': 'Finally, what is your email address? This will be your username in the system.',
    'registration.step.agreement': '',
    'registration.step.completion': 'Now your patients can choose to talk to PACA at any time and in any language and she will convey the information you gave her.',
    'registration.progress.title': 'Registration Progress',
    'registration.button.back': 'Go Back',
    'registration.button.restart': 'New Therapist Registration',
  },
  he: {
    'nav.whatIsPaca': 'מידע',
    'nav.joinTherapist': 'הצטרפות מטפל',
    'nav.pricing': 'מחירון',
    'nav.sharePaca': 'שתף',
    'nav.therapistPortal': 'איזור אישי למטפלים',
    'nav.backHome': 'חזור לעמוד הבית',
    'search.placeholder': 'חפש מטפל לפי שם מלא או טלפון...',
    'chat.title': 'צ\'אט PACA',
    'chat.placeholder': 'כתוב כאן את השאלה או הבקשה שלך...',
    'chat.welcome': 'שלום! אני PACA, עוזר הבריאות הדיגיטלי שלך. אני רואה שבחרת לשוחח עם',
    'chat.selectTherapist': 'היי, אני מערכת PACA, אוכל לעזור לך למצוא מטפל מתאים לבעייתך. בואו נתחיל - מה השם המלא שלך?',
    'chat.howCanHelp': 'איך אוכל לעזור לך היום?',
    'chat.error': 'מצטער, יש בעיה טכנית. נסה שוב.',
    'chat.voiceRecorded': 'הודעה קולית נקלטה...',
    'chat.newChat': 'צ\'אט חדש',
    'about.title': 'מידע',
    'about.desc1': 'פאקא הינה מערכת תקשורת בין מטופלים ומטפלים ברפואה. כל סוגי הרפואה. המיוחד בפאקא שהיא הופכת למזכירה אישית אינטראקטיבית שלך לאחר הרשמה קצרה של מספר דקות ללא עלות. את המידע שתעביר לה ורק אותו פאקא תמסור בצורה נעימה וסובלנית לכל מי שישאל אותה 24 שעות ביממה 7 ימים בשבוע בכל שפה שבה תישאל. במידה והיא תשאל שאלה אשר אין בידה מידע מספיק לענות, היא תשאל אותך ותחזיר תשובה למטופל. הכל בהודעות ואטסאפ. בלי שאלות חוזרות של מטופלים, בלי בלבולי מח בטלפון ובלי מערכת טלפוניה מסורבלת. בכל שעה, בכל זמן והמטופלים מרוצים! הצטרף אלינו בחינם וחסוך זמן יקר...',
    'therapist.title': 'Therapist Personal Area',
    'therapist.login': 'Login to access your personal area',
    'therapist.username': 'Username (Email)',
    'therapist.password': 'Password',
    'therapist.loginBtn': 'Login',
    'therapist.loginBtnLoading': 'Logging in...',
    'therapist.logout': 'Logout',
    'therapist.welcome': 'Welcome',
    'therapist.error': 'Error',
    'therapist.loginError': 'Login Error',
    'therapist.loginSuccess': 'Login Successful',
    'therapist.usernameRequired': 'Please enter username and password',
    'therapist.wrongCredentials': 'Incorrect username or password',
    'therapist.loginFailed': 'An error occurred during login',
    'therapist.tabs.addNote': 'Add Information',
    'therapist.tabs.retakeSurvey': 'Retake Survey',
    'therapist.tabs.editDetails': 'Edit Details',
    'therapist.tabs.analytics': 'Analytics',
    'therapist.tabs.reviews': 'Reviews',
    'therapist.tabs.addRecommendations': 'Add Recommendations',
    'therapist.tabs.subscription': 'Subscription Type',
    'pricing.title': 'תכניות מחירים',
    'pricing.desc': 'האפליקציה חינם למטופלים ולמטפלים מתחילים',
    'pricing.basic': 'חבילת בסיס',
    'pricing.free': 'חינם!',
    'pricing.basicDesc': 'חינם למטפלים',
    'pricing.noCreditCard': 'ללא כרטיס אשראי',
    'pricing.feature1': 'מענה 24/7 למטופלים בשפות שונות',
    'pricing.feature2': 'צ\'אט חכם עם מטופלים',
    'pricing.feature3': 'ניהול פרופיל מטפל',
    'pricing.business': 'חבילת עסקים',
    'pricing.businessPrice': '$8',
    'pricing.businessPriceUnit': '/חודש',
    'pricing.businessDesc': 'כלים מקצועיים לפרקטיקה צומחת',
    'pricing.businessFeature1': 'הפניה ל-CRM',
    'pricing.businessFeature2': 'שמירת כל הפניות - ללא איבוד לידים',
    'pricing.businessFeature3': 'מענה מהיר של המערכת',
    'pricing.businessFeature4': 'תמיכה בשעות העבודה',
    'pricing.businessFeature5': 'שיווק מטפל',
    'pricing.premium': 'חבילת פרימיום',
    'pricing.premiumPrice': '$250',
    'pricing.premiumPriceUnit': '/חודש',
    'pricing.premiumDesc': 'פתרון מלא עם אווטאר AI',
    'pricing.premiumFeature1': 'אווטאר AI אישי',
    'pricing.premiumFeature2': 'מענה 24/7 למטופלים',
    'pricing.premiumFeature3': 'שיווק דיגיטלי',
    'pricing.premiumFeature4': 'המלצות AI לשיפור התדמית',
    'pricing.premiumFeature5': 'שיפור תמונת תדמית',
    'pricing.premiumFeature6': 'חיבורים ל-CRM שונים ולמזכירת AI',
    'pricing.allBasicFeatures': 'כל תכונות חבילת הבסיס',
    'pricing.allBusinessFeatures': 'כל תכונות חבילת העסקים',
    'pricing.whyPaca': 'למה PACA?',
    'pricing.whyPacaDesc': 'כי אנחנו מאמינים בשירות לקהילת המטופלים והמטפלים ברפואה משלימה. האתר נועד לחסוך לך שעות רבות של הסברים למטופלים על שיטת עבודתך וכיצד אתה יכול לעזור להם.',
    'pricing.costCalculator': 'מחשבון עלויות וזמן',
    'pricing.costs': 'עלויות',
    'pricing.costExample1': 'בסיס: חינם - שאלות מטופלים ללא הגבלה',
    'pricing.costExample2': 'עסקים: $8/חודש - אינטגרציית CRM ושיווק',
    'pricing.costExample3': 'פרימיום: $250/חודש - אווטאר AI מלא וחבילת שיווק',
    'pricing.timeSaved': 'זמן שנחסך',
    'pricing.timeExample1': 'כל שאלה חוסכת לך כחצי דקה הסבר',
    'pricing.timeExample2': '100 שאלות = 50 דקות נחסכות בחודש',
    'pricing.timeExample3': '200 שאלות = 100 דקות (1.7 שעות) נחסכות בחודש',
    'pricing.signUpFree': 'הרשמה חינם',
    'pricing.upgradeBusiness': 'שדרוג לחבילת עסקים',
    'pricing.upgradePremium': 'שדרוג לפרימיום',
    'share.title': 'שתף את PACA',
    'share.desc': 'עזור לאחרים לגלות את המערכת שלנו',
    'share.email': 'אימייל',
    'share.message': 'בדוק את PACA - מערכת תקשורת מתקדמת בין מטופלים ומטפלים',
    'share.emailSubject': 'PACA - מערכת תקשורת רפואית',
    'share.howToShare': 'איך לשתף?',
    'share.howToShareDesc': 'בחר את הפלטפורמה המועדפת עליך ושתף את PACA עם חברים וקולגות',
    'share.tips': 'טיפים לשיתוף יעיל',
    'share.tip1': 'שתף עם קולגות מטפלים שיכולים להפיק תועלת מהמערכת',
    'share.tip2': 'ספר על החוויה האישית שלך עם PACA',
    'share.tip3': 'הדגש את הערך שהמערכת מביאה למטופלים ולמטפלים',
    'notFound.title': 'עמוד לא נמצא',
    'notFound.desc': 'העמוד שאתה מחפש אינו קיים',
    'registration.title': 'רישום מטפל',
    'registration.desc': 'הצטרף לרשת אנשי הבריאות שלנו'
  }
};

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    return saved || 'he';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Set document direction based on language
    document.documentElement.dir = language === 'he' || language === 'ar' ? 'rtl' : 'ltr';
    // Set document language attribute
    document.documentElement.lang = language;
  }, [language]);

  const t = (key: string): string => {
    return translations[language]?.[key] || translations['he']?.[key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};